import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Wine, Grape, MapPin, Star, MessageSquare } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "../hooks/use-toast";
import { API } from "../lib/api";
import { WINES_BY_COLOR, WINE_COLOR_ORDER, WINE_COLOR_ACCENT } from "../lib/wines";

// Spirits available — same data structure as in PartnersSection.
const SPIRITS_BY_FAMILY = {
  cognac: [
    { name: "Camus VS", detail: "70 cl · 40°" },
    { name: "Crocodile Bros XO", detail: "70 cl · 42° · XO" },
    { name: "Delamain Pale & Dry", detail: "70 cl · 42°" },
  ],
  armagnac: [
    { name: "Bas Armagnac Château de Lacquy", detail: "70 cl · 40,5° · 7 ans d'âge" },
    { name: "Bas Armagnac Château de Lacquy", detail: "70 cl · 43° · 12 ans d'âge" },
  ],
  whisky: [
    { name: "Jack Daniel's Tennessee Whiskey", detail: "70 cl · 40°" },
    { name: "Michel Couvreur Clearach", detail: "70 cl · 43° · Single Malt" },
    { name: "MBK « Verre Printanier »", detail: "70 cl" },
  ],
  rhum: [
    { name: "Havana Club Especial Ambré", detail: "70 cl · 40°" },
    { name: "Savanna Traditionnel Ambré", detail: "70 cl · 5 ans · Réunion" },
    { name: "Bielle Rhum Vieux", detail: "70 cl · 41° · 3 ans · Marie-Galante" },
    { name: "La Compagnie des Indes Blended", detail: "70 cl · 40° · Caraïbes" },
    { name: "Cachaça Aguacana", detail: "70 cl · 37,5°" },
  ],
  vodka: [
    { name: "Vodka Romanov", detail: "70 cl · 40°" },
  ],
  gin: [
    { name: "Distillerie Maralpa — Gin Signature", detail: "50 cl · 43°" },
    { name: "Pegasus Distillerie — Gin Orion", detail: "70 cl" },
  ],
  liqueurs: [
    { name: "Chartreuse Verte", detail: "70 cl · 55°" },
    { name: "Get 27", detail: "1 L · 17,9°" },
    { name: "Limoncello Sorrentini", detail: "1 L · 18°" },
    { name: "Liqueur d'Abricot Baillot Arnaud", detail: "50 cl · 15°" },
    { name: "Liqueur de Citron · Maralpa", detail: "50 cl · 28,5°" },
    { name: "Liqueur de Clémentine · Maralpa", detail: "50 cl · 31,5°" },
    { name: "Liqueur de Mandarine · Maralpa", detail: "50 cl · 30,3°" },
    { name: "Liqueur de Menthe · Maralpa", detail: "50 cl · 26°" },
    { name: "Liqueur de Verveine · Maralpa", detail: "50 cl · 31°" },
    { name: "Liqueur de Gentiane · Maralpa", detail: "50 cl · 22,7°" },
    { name: "L'Amer · Maralpa", detail: "50 cl · 24°" },
  ],
  porto: [
    { name: "Porto Taylor's Chip Dry · Blanc", detail: "75 cl" },
    { name: "Porto Taylor's Select · Rouge", detail: "75 cl" },
  ],
};

const SPIRIT_FAMILIES = [
  { id: "cognac", color: "#8B6F4E" },
  { id: "armagnac", color: "#A6503C" },
  { id: "whisky", color: "#5D544F" },
  { id: "rhum", color: "#C1624D" },
  { id: "vodka", color: "#7A8AB0" },
  { id: "gin", color: "#6F7D4E" },
  { id: "liqueurs", color: "#A5C4D4" },
  { id: "porto", color: "#8B3A3A" },
];

const RegionCard = ({ region, index, isInView }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={isInView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.4, delay: index * 0.08 }}
    className="group p-5 border-b border-[#D8D0C5] last:border-b-0 hover:bg-[#F5E6D3]/30 
               transition-colors duration-300 rounded-lg"
    data-testid={`region-${region.name.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <h4
            className="text-xl font-normal text-[#2C2420] group-hover:text-[#C1624D] transition-colors duration-300"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {region.name}
          </h4>
          <span
            className="px-2 py-0.5 text-xs uppercase tracking-wider rounded-full"
            style={{
              backgroundColor: `${region.color}12`,
              color: region.color,
              fontFamily: "'Lato', sans-serif",
            }}
          >
            {region.expertise}
          </span>
        </div>
        <p
          className="text-sm text-[#8C847F] leading-relaxed"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {region.description}
        </p>
      </div>
      <Grape size={20} className="text-[#D8D0C5] group-hover:text-[#C1624D] transition-colors duration-300 flex-shrink-0 mt-1" />
    </div>
  </motion.div>
);

const ConsultationForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", budget: "", preferences: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/wine-consultation`, form);
      setSent(true);
      setForm({ name: "", email: "", budget: "", preferences: "", message: "" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("toast.error_title"),
        description: t("toast.error_consultation"),
      });
    }
    setSending(false);
  };

  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="py-12 text-center"
        data-testid="consultation-success"
      >
        <div className="w-16 h-16 rounded-full bg-[#6F7D4E]/10 flex items-center justify-center mx-auto mb-4">
          <Wine size={24} className="text-[#6F7D4E]" />
        </div>
        <p className="text-xl text-[#2C2420] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {t("cellar.consultation_success")}
        </p>
        <p className="text-sm text-[#8C847F]" style={{ fontFamily: "'Lato', sans-serif" }}>
          {t("cellar.consultation_success_desc")}
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-6 text-sm text-[#C1624D] underline underline-offset-4 hover:opacity-70 transition-opacity"
          data-testid="consultation-another-btn"
        >
          {t("cellar.consultation_another")}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="consultation-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
            {t("cellar.consultation_name")}
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                       focus:outline-none focus:border-[#C1624D] transition-colors duration-300"
            style={{ fontFamily: "'Lato', sans-serif" }}
            placeholder={t("cellar.consultation_name_placeholder")}
            data-testid="consultation-name-input"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
            {t("cellar.consultation_email")}
          </label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                       focus:outline-none focus:border-[#C1624D] transition-colors duration-300"
            style={{ fontFamily: "'Lato', sans-serif" }}
            placeholder={t("cellar.consultation_email_placeholder")}
            data-testid="consultation-email-input"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
          {t("cellar.consultation_budget")}
        </label>
        <input
          type="text"
          value={form.budget}
          onChange={(e) => setForm({ ...form, budget: e.target.value })}
          className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                     focus:outline-none focus:border-[#C1624D] transition-colors duration-300"
          style={{ fontFamily: "'Lato', sans-serif" }}
          placeholder={t("cellar.consultation_budget_placeholder")}
          data-testid="consultation-budget-input"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
          {t("cellar.consultation_preferences")}
        </label>
        <textarea
          rows={3}
          value={form.preferences}
          onChange={(e) => setForm({ ...form, preferences: e.target.value })}
          className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                     focus:outline-none focus:border-[#C1624D] transition-colors duration-300 resize-none"
          style={{ fontFamily: "'Lato', sans-serif" }}
          placeholder={t("cellar.consultation_preferences_placeholder")}
          data-testid="consultation-preferences-input"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-[#8C847F] mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
          {t("cellar.consultation_message")}
        </label>
        <textarea
          rows={2}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full px-4 py-3 bg-[#FDFBF7] border border-[#D8D0C5] rounded-sm text-[#2C2420]
                     focus:outline-none focus:border-[#C1624D] transition-colors duration-300 resize-none"
          style={{ fontFamily: "'Lato', sans-serif" }}
          placeholder={t("cellar.consultation_message_placeholder")}
          data-testid="consultation-message-input"
        />
      </div>
      <Button
        type="submit"
        disabled={sending}
        className="w-full py-3 rounded-full bg-[#6F7D4E] text-white font-serif italic text-lg
                   hover:bg-[#5A6840] active:scale-[0.98] transition-all duration-300 shadow-md hover:shadow-lg h-auto"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
        data-testid="consultation-submit-btn"
      >
        {sending ? t("cellar.consultation_sending") : t("cellar.consultation_btn")}
      </Button>
    </form>
  );
};

export const CavisteSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeTab, setActiveTab] = useState("carte");

  const regions = [
    { name: "Alsace", description: t("cellar.region_alsace_desc"), expertise: t("cellar.region_expert"), color: "#C1624D" },
    { name: "Loire", description: t("cellar.region_loire_desc"), expertise: t("cellar.region_expert"), color: "#C1624D" },
    { name: "Bourgogne", description: t("cellar.region_bourgogne_desc"), expertise: t("cellar.region_expert"), color: "#C1624D" },
    { name: "Côtes du Rhône Nord", description: t("cellar.region_rhone_nord_desc"), expertise: t("cellar.region_expert"), color: "#6F7D4E" },
    { name: "Côtes du Rhône Sud", description: t("cellar.region_rhone_sud_desc"), expertise: t("cellar.region_expert"), color: "#6F7D4E" },
    { name: "Sud de la France", description: t("cellar.region_sud_desc"), expertise: t("cellar.region_specialist"), color: "#A5C4D4" },
  ];

  const conseilItems = [
    { icon: MessageSquare, title: t("cellar.conseil_item1_title"), desc: t("cellar.conseil_item1_desc") },
    { icon: Star, title: t("cellar.conseil_item2_title"), desc: t("cellar.conseil_item2_desc") },
    { icon: Wine, title: t("cellar.conseil_item3_title"), desc: t("cellar.conseil_item3_desc") },
    { icon: Grape, title: t("cellar.conseil_item4_title"), desc: t("cellar.conseil_item4_desc") },
  ];

  return (
    <section
      id="caviste"
      data-testid="caviste-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#F5E6D3]/40"
    >
      <div className="max-w-7xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#6F7D4E] mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("cellar.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="caviste-heading"
          >
            {t("cellar.heading")}
          </h2>
          <div className="ornament-divider max-w-xs mx-auto">
            <span className="text-[#6F7D4E] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>&bull;</span>
          </div>
          <p
            className="text-base text-[#8C847F] mt-4 max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("cellar.subtitle")}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList
              className="w-full max-w-2xl mx-auto grid grid-cols-2 sm:flex bg-transparent h-auto p-0 mb-10 md:mb-14 gap-x-4 gap-y-2 sm:gap-0 sm:border-b border-[#D8D0C5] rounded-none"
              data-testid="caviste-tabs"
            >
              <TabsTrigger
                value="carte"
                data-testid="caviste-tab-carte"
                className="sm:flex-1 pb-2 sm:pb-3 rounded-none text-[11px] sm:text-sm md:text-base uppercase tracking-[0.1em] sm:tracking-[0.15em] 
                           text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                           data-[state=active]:text-[#6F7D4E] data-[state=active]:border-[#6F7D4E]
                           data-[state=active]:bg-transparent data-[state=active]:shadow-none
                           hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[36px] leading-tight text-center"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
              >
                {t("cellar.tab_carte")}
              </TabsTrigger>
              <TabsTrigger
                value="vins"
                data-testid="caviste-tab-vins"
                className="sm:flex-1 pb-2 sm:pb-3 rounded-none text-[11px] sm:text-sm md:text-base uppercase tracking-[0.1em] sm:tracking-[0.15em] 
                           text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                           data-[state=active]:text-[#6F7D4E] data-[state=active]:border-[#6F7D4E]
                           data-[state=active]:bg-transparent data-[state=active]:shadow-none
                           hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[36px] leading-tight text-center"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
              >
                {t("cellar.tab_vins")}
              </TabsTrigger>
              <TabsTrigger
                value="conseil"
                data-testid="caviste-tab-conseil"
                className="sm:flex-1 pb-2 sm:pb-3 rounded-none text-[11px] sm:text-sm md:text-base uppercase tracking-[0.1em] sm:tracking-[0.15em] 
                           text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                           data-[state=active]:text-[#6F7D4E] data-[state=active]:border-[#6F7D4E]
                           data-[state=active]:bg-transparent data-[state=active]:shadow-none
                           hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[36px] leading-tight text-center"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
              >
                {t("cellar.tab_conseil")}
              </TabsTrigger>
              <TabsTrigger
                value="spiritueux"
                data-testid="caviste-tab-spiritueux"
                className="sm:flex-1 pb-2 sm:pb-3 rounded-none text-[11px] sm:text-sm md:text-base uppercase tracking-[0.1em] sm:tracking-[0.15em] 
                           text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                           data-[state=active]:text-[#6F7D4E] data-[state=active]:border-[#6F7D4E]
                           data-[state=active]:bg-transparent data-[state=active]:shadow-none
                           hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[36px] leading-tight text-center"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
              >
                {t("cellar.tab_spiritueux")}
              </TabsTrigger>
            </TabsList>

            {/* Carte des Vins Tab */}
            <TabsContent value="carte" data-testid="caviste-content-carte">
              <div className="max-w-5xl mx-auto px-2 sm:px-4">
                {/* Intro */}
                <div className="text-center mb-12">
                  <h3
                    className="text-3xl md:text-4xl font-light text-[#2C2420] mb-4 leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t("cellar.wine_list_heading")}
                  </h3>
                  <p
                    className="text-base text-[#5D544F] leading-relaxed max-w-2xl mx-auto"
                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                  >
                    {t("cellar.wine_list_intro")}
                  </p>
                  <p
                    className="text-sm text-[#8C847F] italic mt-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
                  >
                    {t("cellar.wine_list_prices_note")}
                  </p>
                </div>

                {/* Wine list — tabbed by color (Champagne/Rosé/Blanc/Rouge) */}
                <Tabs defaultValue="champagne" className="w-full">
                  <TabsList
                    className="w-full max-w-2xl mx-auto flex flex-wrap justify-center bg-transparent h-auto p-0 mb-10 gap-2 sm:gap-0 sm:border-b border-[#D8D0C5] rounded-none"
                    data-testid="wines-color-tabs"
                  >
                    {WINE_COLOR_ORDER.map((colorId) => {
                      const groups = WINES_BY_COLOR[colorId] || [];
                      const count = groups.reduce((s, g) => s + g.wines.length, 0);
                      return (
                        <TabsTrigger
                          key={colorId}
                          value={colorId}
                          data-testid={`wines-color-tab-${colorId}`}
                          className="basis-[calc(50%-8px)] sm:basis-auto sm:flex-shrink-0 sm:px-6 pb-2 sm:pb-3 rounded-none text-sm sm:text-base uppercase tracking-[0.15em]
                                     text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                                     data-[state=active]:border-[#C1624D]
                                     data-[state=active]:bg-transparent data-[state=active]:shadow-none
                                     hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[40px] leading-tight text-center"
                          style={{
                            fontFamily: "'Lato', sans-serif",
                            fontWeight: 400,
                            color: undefined,
                            "--color-accent": WINE_COLOR_ACCENT[colorId],
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: WINE_COLOR_ACCENT[colorId] }}
                            />
                            <span>{t(`cellar.wine_color_tab_${colorId}`)}</span>
                            <span className="text-[10px] text-[#8C847F] font-light">({count})</span>
                          </span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {WINE_COLOR_ORDER.map((colorId) => {
                    const groups = WINES_BY_COLOR[colorId] || [];
                    const accent = WINE_COLOR_ACCENT[colorId];
                    return (
                      <TabsContent
                        key={colorId}
                        value={colorId}
                        data-testid={`wines-color-content-${colorId}`}
                        className="space-y-10"
                      >
                        {groups.map((group) => (
                          <div
                            key={group.subregion}
                            data-testid={`wines-subregion-${colorId}-${group.subregion}`}
                          >
                            {/* Sub-region header */}
                            <div className="flex items-baseline gap-3 mb-5 pb-2 border-b border-[#D8D0C5]">
                              <div
                                className="w-1.5 h-5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: accent }}
                              />
                              <h4
                                className="text-xl md:text-2xl font-light text-[#2C2420]"
                                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                              >
                                {group.subregion}
                              </h4>
                              <span
                                className="ml-auto text-xs text-[#8C847F]"
                                style={{ fontFamily: "'Lato', sans-serif" }}
                              >
                                {group.wines.length}
                              </span>
                            </div>

                            {/* Wine lines */}
                            <ul className="space-y-2">
                              {group.wines.map((w, i) => (
                                <li
                                  key={`${w.domain}-${w.cuvee || ""}-${w.year || ""}-${i}`}
                                  className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 py-1"
                                  data-testid={`wine-${colorId}-${i}`}
                                >
                                  <span
                                    className="text-base text-[#2C2420] font-normal"
                                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                                  >
                                    {w.domain}
                                    {w.cuvee && (
                                      <span className="italic text-[#5D544F]"> · {w.cuvee}</span>
                                    )}
                                  </span>
                                  <span
                                    className="hidden sm:block flex-1 border-b border-dotted border-[#D8D0C5]"
                                    style={{ minHeight: "1px" }}
                                  />
                                  <span
                                    className="text-sm text-[#5D544F]"
                                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                                  >
                                    {w.appellation}
                                    {w.year && <span className="text-[#8C847F]">{` · ${w.year}`}</span>}
                                    {w.magnum && (
                                      <span className="ml-2 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#8B3A3A]/10 text-[#8B3A3A] rounded">
                                        Magnum
                                      </span>
                                    )}
                                    {w.bio && (
                                      <span className="ml-2 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#6F7D4E]/10 text-[#6F7D4E] rounded">
                                        Bio
                                      </span>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </TabsContent>
                    );
                  })}
                </Tabs>

                {/* Tarif resto + caviste reminder */}
                <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <div className="p-5 bg-white/70 border border-[#D8D0C5] rounded-sm">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#C1624D] mb-2"
                       style={{ fontFamily: "'Lato', sans-serif", fontWeight: 500 }}>
                      {t("cellar.tarif_resto")}
                    </p>
                    <p className="text-sm text-[#5D544F]"
                       style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}>
                      {t("cellar.tarif_resto_desc")}
                    </p>
                  </div>
                  <div className="p-5 bg-white/70 border border-[#D8D0C5] rounded-sm">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#6F7D4E] mb-2"
                       style={{ fontFamily: "'Lato', sans-serif", fontWeight: 500 }}>
                      {t("cellar.tarif_caviste")}
                    </p>
                    <p className="text-sm text-[#5D544F]"
                       style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}>
                      {t("cellar.tarif_caviste_desc")}
                    </p>
                  </div>
                </div>
                <p
                  className="text-sm text-[#8C847F] italic mt-8 text-center"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
                >
                  {t("cellar.carte_fallback")}
                </p>
              </div>
            </TabsContent>

            {/* Vins Tab */}
            <TabsContent value="vins" data-testid="caviste-content-vins">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
                {/* Left: intro */}
                <div className="lg:col-span-2">
                  <div className="relative overflow-hidden rounded-sm mb-8">
                    <img
                      src="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=600&h=500&fit=crop&q=80"
                      alt="Sélection de vins"
                      className="w-full h-[300px] lg:h-[380px] object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3
                    className="text-2xl font-light text-[#2C2420] mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t("cellar.vins_heading")}
                  </h3>
                  <p
                    className="text-base text-[#5D544F] leading-relaxed mb-4"
                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                  >
                    {t("cellar.vins_desc")}
                  </p>
                  <div className="flex items-center gap-3 p-4 bg-white/60 border border-[#D8D0C5]/50 rounded-sm">
                    <MapPin size={16} className="text-[#C1624D] flex-shrink-0" />
                    <p className="text-sm text-[#5D544F]" style={{ fontFamily: "'Lato', sans-serif" }}>
                      {t("cellar.vins_visit")}
                    </p>
                  </div>
                  <div className="mt-4 p-4 bg-[#6F7D4E]/10 border border-[#6F7D4E]/20 rounded-sm">
                    <p
                      className="text-sm text-[#5D544F] italic text-center"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
                    >
                      {t("cellar.vins_takeaway_note")}
                    </p>
                  </div>
                </div>

                {/* Right: regions */}
                <div className="lg:col-span-3">
                  <p
                    className="text-xs uppercase tracking-[0.2em] text-[#8C847F] mb-6"
                    style={{ fontFamily: "'Lato', sans-serif" }}
                  >
                    {t("cellar.vins_regions_label")}
                  </p>
                  <div>
                    {regions.map((region, i) => (
                      <RegionCard key={region.name} region={region} index={i} isInView={isInView} />
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Conseil Tab */}
            <TabsContent value="conseil" data-testid="caviste-content-conseil">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
                {/* Left: description */}
                <div>
                  <div className="relative overflow-hidden rounded-sm mb-8">
                    <img
                      src="https://images.unsplash.com/photo-1771626965774-3392c95171ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx3aW5lJTIwdGFzdGluZyUyMHNvbW1lbGllciUyMGNlbGxhcnxlbnwwfHx8fDE3NzI2MDkzOTJ8MA&ixlib=rb-4.1.0&q=85"
                      alt="Dégustation et conseil en vins"
                      className="w-full h-[280px] object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3
                    className="text-2xl md:text-3xl font-light text-[#2C2420] mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t("cellar.conseil_heading")}
                  </h3>
                  <p
                    className="text-base text-[#5D544F] leading-relaxed mb-6"
                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                  >
                    {t("cellar.conseil_desc")}
                  </p>

                  {/* Service details */}
                  <div className="space-y-4">
                    {conseilItems.map((item) => (
                      <div key={item.title} className="flex items-start gap-4 group">
                        <div className="w-10 h-10 rounded-full bg-[#6F7D4E]/10 flex items-center justify-center flex-shrink-0">
                          <item.icon size={18} className="text-[#6F7D4E]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#2C2420]" style={{ fontFamily: "'Lato', sans-serif" }}>
                            {item.title}
                          </p>
                          <p className="text-sm text-[#8C847F]" style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}>
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: consultation form */}
                <div>
                  <div className="bg-white/80 backdrop-blur-sm p-8 md:p-10 border border-[#D8D0C5] rounded-sm shadow-sm">
                    <h3
                      className="text-2xl font-light text-[#2C2420] mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {t("cellar.consultation_heading")}
                    </h3>
                    <p
                      className="text-sm text-[#8C847F] mb-8"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                    >
                      {t("cellar.consultation_subtitle")}
                    </p>
                    <ConsultationForm />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Spiritueux Tab */}
            <TabsContent value="spiritueux" data-testid="caviste-content-spiritueux">
              <div className="max-w-5xl mx-auto px-2 sm:px-4">
                {/* Intro */}
                <div className="text-center mb-12">
                  <h3
                    className="text-3xl md:text-4xl font-light text-[#2C2420] mb-4 leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {t("cellar.spirits_heading")}
                  </h3>
                  <p
                    className="text-base text-[#5D544F] leading-relaxed max-w-2xl mx-auto"
                    style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                  >
                    {t("cellar.spirits_desc")}
                  </p>
                </div>

                {/* Spirits — tabbed by family (same UX as wines) */}
                <Tabs defaultValue="cognac" className="w-full">
                  <TabsList
                    className="w-full max-w-3xl mx-auto flex flex-wrap justify-center bg-transparent h-auto p-0 mb-10 gap-2 sm:gap-0 sm:border-b border-[#D8D0C5] rounded-none"
                    data-testid="spirits-family-tabs"
                  >
                    {SPIRIT_FAMILIES.map((fam) => {
                      const list = SPIRITS_BY_FAMILY[fam.id] || [];
                      if (list.length === 0) return null;
                      return (
                        <TabsTrigger
                          key={fam.id}
                          value={fam.id}
                          data-testid={`spirits-family-tab-${fam.id}`}
                          className="basis-[calc(50%-8px)] sm:basis-auto sm:flex-shrink-0 sm:px-5 pb-2 sm:pb-3 rounded-none text-sm sm:text-base uppercase tracking-[0.15em]
                                     text-[#8C847F] border-b-2 border-[#D8D0C5] sm:border-transparent
                                     data-[state=active]:border-[#C1624D]
                                     data-[state=active]:bg-transparent data-[state=active]:shadow-none
                                     hover:text-[#5D544F] transition-colors duration-300 whitespace-normal h-auto min-h-[40px] leading-tight text-center"
                          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
                        >
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: fam.color }}
                            />
                            <span>{t(`cellar.spirit_family_${fam.id}`)}</span>
                            <span className="text-[10px] text-[#8C847F] font-light">({list.length})</span>
                          </span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {SPIRIT_FAMILIES.map((fam) => {
                    const list = SPIRITS_BY_FAMILY[fam.id] || [];
                    if (list.length === 0) return null;
                    return (
                      <TabsContent
                        key={fam.id}
                        value={fam.id}
                        data-testid={`spirits-family-content-${fam.id}`}
                        className="max-w-3xl mx-auto"
                      >
                        {/* Family header */}
                        <div className="flex items-baseline gap-3 mb-5 pb-2 border-b border-[#D8D0C5]">
                          <div
                            className="w-1.5 h-5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: fam.color }}
                          />
                          <h4
                            className="text-xl md:text-2xl font-light text-[#2C2420]"
                            style={{ fontFamily: "'Cormorant Garamond', serif" }}
                          >
                            {t(`cellar.spirit_family_${fam.id}`)}
                          </h4>
                          <span
                            className="ml-auto text-xs text-[#8C847F]"
                            style={{ fontFamily: "'Lato', sans-serif" }}
                          >
                            {list.length}
                          </span>
                        </div>

                        <ul className="space-y-2">
                          {list.map((item, i) => (
                            <li
                              key={item.name + item.detail + i}
                              className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 py-1"
                              data-testid={`spirits-item-${fam.id}-${i}`}
                            >
                              <span
                                className="text-base text-[#2C2420] font-normal"
                                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                              >
                                {item.name}
                              </span>
                              <span
                                className="hidden sm:block flex-1 border-b border-dotted border-[#D8D0C5]"
                                style={{ minHeight: "1px" }}
                              />
                              {item.detail && (
                                <span
                                  className="text-sm text-[#5D544F]"
                                  style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                                >
                                  {item.detail}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </TabsContent>
                    );
                  })}
                </Tabs>

                <div className="mt-10 p-5 bg-white/60 border border-[#D8D0C5]/50 rounded-sm max-w-2xl mx-auto">
                  <p
                    className="text-sm text-[#5D544F] italic text-center"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
                  >
                    {t("cellar.spirits_note")}
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
};
